import React from "react";
import Modal, { Props } from "react-modal";
import { twMerge } from "tailwind-merge";

interface IModalV2Props extends Omit<Props, "className" | "overlayClassName"> {
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  /** Header title. Omit to render no header title. */
  title?: string;
  /** Merged with the default title classes — e.g. pass `text-center` to center it. */
  titleClassName?: string;
  /** `true` renders a native close (×) button, wired to `onRequestClose`, in the header row. Omit/`false` to render none. */
  closeButton?: boolean;
  /** Merged with the default close button classes. */
  closeButtonClassName?: string;
  /** Merged with the default header row classes. The header (title and/or close button) only renders when `title` or `closeButton` is set. */
  headerClassName?: string;
}

const DEFAULT_OVERLAY_CLASSES =
  "fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-[3px]";

const DEFAULT_CONTENT_CLASSES =
  "w-fit h-fit rounded-[20px] border-[0.5px] border-solid border-[#E5E7EB] bg-white p-6 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.08),0_2px_8px_0_rgba(0,0,0,0.06)] outline-none";

const DEFAULT_HEADER_CLASSES = "flex items-center gap-4 mb-4";

const DEFAULT_TITLE_CLASSES =
  "flex-1 font-['Mulish'] text-base font-semibold text-[#20332f]";

const DEFAULT_CLOSE_BUTTON_CLASSES =
  "flex items-center justify-center ml-auto shrink-0 font-['Mulish'] text-2xl leading-none text-[#2d2d2d] cursor-pointer bg-[#F3F4F6] px-3 py-2 rounded-full";

export const ModalV2 = ({
  children,
  className,
  overlayClassName,
  title,
  titleClassName,
  closeButton,
  closeButtonClassName,
  headerClassName,
  onRequestClose,
  ...rest
}: IModalV2Props) => {
  const showHeader = Boolean(title) || Boolean(closeButton);

  return (
    <Modal
      {...rest}
      onRequestClose={onRequestClose}
      className={twMerge(DEFAULT_CONTENT_CLASSES, className)}
      overlayClassName={twMerge(DEFAULT_OVERLAY_CLASSES, overlayClassName)}
    >
      {showHeader && (
        <div className={twMerge(DEFAULT_HEADER_CLASSES, headerClassName)}>
          {title && (
            <p className={twMerge(DEFAULT_TITLE_CLASSES, titleClassName)}>
              {title}
            </p>
          )}
          {closeButton && (
            <button
              type="button"
              aria-label="Fechar"
              onClick={(event) => onRequestClose?.(event)}
              className={twMerge(
                DEFAULT_CLOSE_BUTTON_CLASSES,
                closeButtonClassName,
              )}
            >
              ×
            </button>
          )}
        </div>
      )}
      {children}
    </Modal>
  );
};
